import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { FieldValues, useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { getUser } from '../../../../api/client';
import {
  upsertPosition, selectPositionEditing
} from './edit-position-slice';
import { NumericFormat } from 'react-number-format';
import { setSelectedPosition } from '../portfolio-allocation/collapsible-position-slice';
import { recalculatePortfolio } from '../../portfolios-slice';

/**
 * Form to add and edit a position that is custom made by the user, which helds a quantity and a price both managed by the user.
 * It should be initially used for all securities still not treated by Rebalancear.
 */
const EditPositionUserCustom: React.FC = () => {
  // Get the 'dispatch' method from the store
  const dispatch = useAppDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const positionEditing = useAppSelector(selectPositionEditing);
  const isNewPosition = positionEditing?.id ? false : true;

  const [description, setDescription] = useState(positionEditing?.security?.symbol ?? '');

  // React useEffect: applies after rendering. Reset symbol filter when assetTypeIds change.
  useEffect(() => {
    setDescription(positionEditing?.description ?? '');
  }, [positionEditing]);

  const { register, handleSubmit, formState: { errors } } = useForm({
    values: {
      description,
      quantity: positionEditing?.quantity,
      price: positionEditing?.price,
      weight: positionEditing?.weight
    }
  });

  const useOnSubmit = (data: FieldValues) => {
    const execute = async () => {
      const accessToken = await getAccessTokenSilently();
      const user = await getUser(accessToken);
      if (user && positionEditing) {
        positionEditing.description = data['description'];
        positionEditing.quantity = Number.parseInt(data['quantity']);
        positionEditing.price = Number.parseFloat(data['price']);
        positionEditing.weight = Number.parseInt(data['weight']);
        dispatch(upsertPosition({ accessToken, user, position: positionEditing }));
        dispatch(setSelectedPosition(undefined));
        dispatch(recalculatePortfolio({ portfolioId: positionEditing.portfolioId }));
      }
    }
    execute();
  };

  return (
    <div className='flex flex-col w-full z-40'>
      <form onSubmit={handleSubmit(useOnSubmit)} className='w-full flex flex-col text-slate-700'>

        <div className={`flex flex-col mb-4 focus:outline-0`}>
          <label>Descrição</label>
          <input type='text' placeholder='ex: casa, não tem no Rebalancear' className='px-3 py-1 rounded-xl mt-1 border border-neutral-400 outline-slate-800 disabled:bg-gray-300 disabled:text-gray-500'
            {...register("description", { required: false })}
          />
        </div>

        <div className={`flex flex-col mb-4 focus:outline-0`}>
          <label>Quantidade</label>
          <input type='number' className='px-3 py-1 rounded-xl mt-1 border border-neutral-400 outline-slate-800 disabled:bg-gray-300 disabled:text-gray-500'
            {...register("quantity", { required: false })}
          />
        </div>

        <div className={`flex flex-col mb-4 focus:outline-0`}>
          <label>Preço</label>
          {/* <NumericFormat type="text"
            value={positionEditing?.price}
            thousandSeparator='.'
            decimalSeparator=','
            decimalScale={2}
            className='px-3 py-1 rounded-xl mt-1 border border-neutral-400 outline-slate-800 disabled:bg-gray-300 disabled:text-gray-500'
            {...register("price", { required: true, min: { value: 0.01, message: 'O preço deve ser positivo' } })}
          />
          <ErrorMessage errors={errors} name="price" render={({ message }) => <div className='text-red-500 font-semibold'>{message}</div>} /> */}
          <input type='number' className='px-3 py-1 rounded-xl mt-1 border border-neutral-400 outline-slate-800 disabled:bg-gray-300 disabled:text-gray-500'
            {...register("price", { required: true })}
          />
          <ErrorMessage errors={errors} name="price" render={({ message }) => <div className='text-red-500 font-semibold'>{message}</div>} />
        </div>

        <div className='flex flex-col mb-4'>
          <label>Peso</label>
          <input type="number" placeholder="ex: 10" className='px-3 py-1 rounded-xl mt-1 border border-neutral-400 outline-slate-800 disabled:bg-gray-300 disabled:text-gray-500'
            {...register("weight", { required: true, min: { value: 0, message: 'O peso não pode ser negativo' } })}
          />
          <ErrorMessage errors={errors} name="weight" render={({ message }) => <div className='text-red-500 font-semibold'>{message}</div>} />
        </div>

        <div className='flex justify-center items-center mt-4'>
          <input type="submit" value="Enviar" className='cursor-pointer px-4 py-2 text-slate-200 rounded-full bg-slate-800 disabled:cursor-default disabled:bg-gray-600 hover:enabled:opacity-85 hover:enabled:shadow-lg hover:enabled:border-slate-600 hover:enabled:border-1' />
        </div>
      </form >
    </div>
  );
}

export default EditPositionUserCustom;